import { config } from "../config";
import { ApiError } from "./errors";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Platform › Platform plans (owner 2026-10-30, radaccess v1.218):
 *   GET  /access/billing/io.ankhos/platformplans
 *   POST /access/billing/io.ankhos/platformplans
 * superadmin only. A plan billed by a METER: its own band ladder (priced by the
 * tenant's monthly-average active subscribers, within that ladder only) or a
 * cloud plan (included CU / block GB / data-out GB, overage at the shared
 * prices). Console-made rows carry catalogref console/...; the catalog
 * compiler leaves them alone.
 */

export const PLATFORM_NS = "io.ankhos";

export interface PlatformMeter {
  code: string;
  label: string;
  unit: string;
  aggregation: string;
  measured: boolean;
}

export interface PlatformBand {
  componentId: string;
  name: string;
  tier: string;
  subscribersMax?: number;
  price?: number;
  active: boolean;
}

export interface PlatformLadder {
  family: string;
  name: string;
  source: "catalog" | "console";
  bands: PlatformBand[];
}

export interface PlatformCloudPlan {
  componentId: string;
  name: string;
  tier: string;
  price?: number;
  cu: number;
  blockStorageGb: number;
  objectStorageGb: number;
  transferOutGb: number;
  ceilingCu?: number;
  source: "catalog" | "console";
  active: boolean;
}

export interface PlatformOverage {
  meter: string;
  kind: string;
  name: string;
  price?: number;
}

export interface PlatformPlans {
  meters: PlatformMeter[];
  ladders: PlatformLadder[];
  cloudPlans: PlatformCloudPlan[];
  overage: PlatformOverage[];
}

export interface BandIn {
  name: string;
  subscribersMax?: number | undefined;
  price: number;
}

export type PlatformPlanIn =
  | { kind: "ladder"; family: string; name: string; effectiveFrom: string; bands: BandIn[] }
  | {
      kind: "cloud";
      tier: string;
      name: string;
      effectiveFrom: string;
      price: number;
      cu: number;
      blockStorageGb: number;
      objectStorageGb: number;
      transferOutGb: number;
      ceilingCu?: number | undefined;
    };

const path = `/billing/${encodeURIComponent(PLATFORM_NS)}/platformplans`;

export async function getPlatformPlans(identity: CallerIdentity, signal?: AbortSignal): Promise<PlatformPlans> {
  const r = await callGateway<Partial<PlatformPlans>>(identity, config().gatewayBase, path, signal ? { signal } : {});
  const d = r.data ?? {};
  return { meters: d.meters ?? [], ladders: d.ladders ?? [], cloudPlans: d.cloudPlans ?? [], overage: d.overage ?? [] };
}

export async function createPlatformPlan(identity: CallerIdentity, body: PlatformPlanIn): Promise<string[]> {
  const r = await callGateway<{ created?: string[] }>(identity, config().gatewayBase, path, { method: "POST", body });
  return r.data?.created ?? [];
}

/** A whole ladder or one cloud plan. Nothing is deleted. */
export async function withdrawPlatformPlan(identity: CallerIdentity, body: { kind: "ladder"; family: string } | { kind: "cloud"; tier: string }): Promise<void> {
  await callGateway(identity, config().gatewayBase, `${path}/withdraw`, { method: "POST", body });
}

const SLUG = /^[a-z][a-z0-9-]{1,30}[a-z0-9]$/;
const paisa = (p: number) => Number.isFinite(p) && p > 0 && p <= 10_000_000 && Math.round(p * 100) === p * 100;

/** Why a ladder cannot be sent, or "" (mirrors radaccess PlanPlatformRows). */
export function ladderProblem(family: string, name: string, bands: BandIn[]): string {
  if (!SLUG.test(family)) return "Family code: 3-32 lower-case letters, digits and dashes.";
  if (name.trim().length < 2) return "Name the ladder.";
  if (bands.length === 0 || bands.length > 10) return "1 to 10 bands.";
  let prev = 0;
  for (let i = 0; i < bands.length; i++) {
    const b = bands[i]!;
    const last = i === bands.length - 1;
    if (b.name.trim().length < 2) return `Band ${i + 1}: name it.`;
    if (!paisa(b.price)) return `Band ${i + 1}: price more than 0, to the paisa.`;
    if (b.subscribersMax === undefined) {
      if (!last) return `Band ${i + 1}: only the top band may have no upper limit.`;
    } else {
      if (!Number.isInteger(b.subscribersMax) || b.subscribersMax <= prev) return `Band ${i + 1}: "up to" must be more than ${prev}.`;
      prev = b.subscribersMax;
    }
  }
  return "";
}

/** Each band's range in words: 0–300, 301–1000, 1001 and more. */
export function bandRanges(bands: { subscribersMax?: number | undefined }[]): string[] {
  let from = 0;
  return bands.map((b) => {
    const s = b.subscribersMax === undefined ? `${from} and more` : `${from}–${b.subscribersMax}`;
    if (b.subscribersMax !== undefined) from = b.subscribersMax + 1;
    return s;
  });
}

/** Why a cloud plan cannot be sent, or "". */
export function cloudProblem(p: { tier: string; name: string; price: number; cu: number; blockStorageGb: number; transferOutGb: number; ceilingCu?: number | undefined }): string {
  if (!SLUG.test(p.tier)) return "Plan code: 3-32 lower-case letters, digits and dashes.";
  if (p.name.trim().length < 2) return "Name the plan.";
  if (!paisa(p.price)) return "Monthly price more than 0, to the paisa.";
  if (!(p.cu > 0)) return "Included CU must be more than 0.";
  if (p.blockStorageGb < 0 || p.transferOutGb < 0) return "Included GB cannot be negative.";
  if (p.ceilingCu !== undefined && p.ceilingCu < p.cu) return "Ceiling CU must be at least the included CU.";
  return "";
}

export function platformPlanErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return "Only superadmin can make platform plans.";
    const b = (err.body ?? {}) as { error?: unknown; created?: unknown; withdrawn?: unknown };
    if (Array.isArray(b.withdrawn) && typeof b.error === "string") return `${b.error} Press Withdraw again to finish.`;
    const created = Array.isArray(b.created) && b.created.length ? ` Already written: ${b.created.length} row(s) — tell support before retrying.` : "";
    if (typeof b.error === "string") return b.error.replace(/^invalid platform plan: /, "") + created;
  }
  return err instanceof Error ? err.message : String(err);
}
